import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getProgram, enrollIntent } from "@/lib/apiClient";
import { ArrowLeft, ArrowUpRight, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";

export default function ProgramDetail() {
  const { t } = useI18n();
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setP(null); setMissing(false);
    getProgram(id).then(setP).catch(() => setMissing(true));
  }, [id]);

  // A program the catalogue doesn't have (the Learning Journey lists a few that are not offered here): say so, with a way back.
  if (missing) {
    return (
      <div className="min-h-screen flex flex-col" data-testid="program-missing">
        <Header />
        <main className="flex-1 max-w-[560px] mx-auto w-full px-5 py-16 text-center">
          <h1 className="font-heading text-2xl font-semibold text-[#1F2937]">This program isn&apos;t available here yet</h1>
          <p className="mt-3 text-[#4B5563]">It isn&apos;t part of Quran Fluency right now. You can see everything that is on the programs page.</p>
          <button onClick={() => nav("/programs")} data-testid="program-missing-back"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white font-medium transition">
            <ArrowLeft className="w-4 h-4" /> {t("prog.all_programs")}
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  if (!p) return <div className="min-h-screen"><Header /></div>;

  const enroll = async () => {
    try {
      const res = await enrollIntent(p.id);
      toast.success(t("prog.toast_enrolled"));
      if (p.is_in_app && p.enrollment_url?.startsWith("/")) nav(p.enrollment_url);
      else window.open(res.enrollment_url, "_blank", "noreferrer");
    } catch { toast.error(t("prog.toast_enroll_failed")); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid={`program-${p.id}`}>
      <Header />
      <main className="flex-1 max-w-[1024px] mx-auto w-full px-5 sm:px-8 py-10">
        <button onClick={() => nav("/programs")} className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#064E3B]">
          <ArrowLeft className="w-4 h-4" /> {t("prog.all_programs")}
        </button>

        <div className="mt-6 grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">{p.category}</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold mt-2 text-[#1F2937]">{p.title}</h1>
            <p className="text-lg text-[#4B5563] mt-4 leading-relaxed">{p.tagline}</p>

            <div className="mt-8 rounded-2xl bg-white border border-[#E7E0D2] p-6">
              <div className="text-xs uppercase tracking-widest text-[#4B5563]">{t("prog.about")}</div>
              <p className="mt-3 text-[#1F2937] leading-relaxed">{p.description}</p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div className="text-3xl font-heading font-semibold text-[#064E3B]">
                {p.is_free ? t("prog.free") : `$${p.price}`}
                {!p.is_free && <span className="text-sm text-[#4B5563] font-normal ml-2">{p.currency}</span>}
              </div>
              <button
                data-testid="enroll-btn"
                onClick={enroll}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white font-medium transition"
              >
                {p.is_in_app ? t("prog.start_now") : t("prog.enroll_external")}
                {p.is_in_app ? <ArrowRight className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden bg-white border border-[#E7E0D2]">
              {p.image && <img src={p.image} alt={p.title} className="w-full aspect-[4/3] object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
              <div className="p-5">
                <div className="text-xs uppercase tracking-widest text-[#4B5563]">{t("prog.includes")}</div>
                <ul className="mt-3 space-y-2 text-sm text-[#1F2937]">
                  <Li>{t("prog.inc_curriculum")}</Li>
                  <Li>{t("prog.inc_mentor")}</Li>
                  <Li>{t("prog.inc_tracking")}</Li>
                  <Li>{t("prog.inc_certificate")}</Li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Li({ children }) {
  return <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> {children}</li>;
}
